/**
 * The top-right control cluster.
 *
 * A narrow control bar cannot hold every button. The compact rules already
 * drop volume, picture-in-picture and the clock readouts, but casting is not
 * droppable: throwing a stream at a TV is more of a phone gesture than a
 * desktop one, and it is the one control a viewer goes looking for. With it in
 * the bar, a phone-width player ran out of room and wrapped, putting fullscreen
 * on a second row by itself.
 *
 * So the cast buttons MOVE rather than hide. They are lifted out of the bar and
 * parked at the top right, which is where every phone video app puts them, and
 * which is the one corner the player was not already using: the title and the
 * live badge own the top left, the pause block hangs off the same corner, and
 * the unmute pill is at the bottom right on a narrow player.
 *
 * WHY REPARENT RATHER THAN REPOSITION. The control bar is itself absolutely
 * positioned, so it is the containing block for anything inside it: a child
 * pinned to `top: 0` lands at the top of the BAR, not the top of the player.
 * The only way to reach the player's corner is to leave the bar.
 *
 * The buttons are borrowed, not rebuilt. video.js and the Chromecast plugin own
 * them, keep their own listeners and state on them, and rebuild the bar out
 * from under us on a tech switch; moving the same nodes back and forth keeps
 * every one of those intact and means this module owns nothing it could get
 * out of sync with.
 */

/** What may be lifted, in the order it should appear up there. */
const LIFTABLE = ['.lp-airplay-button', '.vjs-chromecast-button', '.vjs-airplay-button']

/** Below this player width the bar is too tight to hold them. */
const COMPACT_PX = 600

export interface TopControlsHandle {
  dispose(): void
}

export function mountTopControls(playerEl: HTMLElement): TopControlsHandle {
  playerEl.querySelector('.lp-top-controls')?.remove()

  const cluster = document.createElement('div')
  cluster.className = 'lp-top-controls'
  playerEl.appendChild(cluster)

  let compact = false
  let disposed = false

  const controlBar = (): Element | null => playerEl.querySelector('.vjs-control-bar')

  /**
   * Put every liftable button where it belongs for the current width. Called
   * on resize, on pointer-type change, and whenever the bar's children change,
   * so a button that appears late — AirPlay waits for a receiver to show up on
   * the network, Chromecast waits for its SDK — is placed as soon as it exists.
   */
  const sync = (): void => {
    if (disposed) return
    const bar = controlBar()
    for (const sel of LIFTABLE) {
      const btn = playerEl.querySelector<HTMLElement>(sel)
      if (!btn) continue
      if (compact) {
        if (btn.parentElement !== cluster) cluster.appendChild(btn)
      } else if (bar && btn.parentElement !== bar) {
        // Back where cast.ts puts it: just before the settings gear, with the
        // other send-elsewhere controls.
        bar.insertBefore(btn, bar.querySelector('.lp-settings-button'))
      }
    }
    // An empty cluster must not reserve space against the title, and a full one
    // must, or a long title slides underneath it.
    const shown = Array.from(cluster.children).some(
      (el) => !(el as HTMLElement).classList.contains('lp-hidden'),
    )
    playerEl.classList.toggle('lp-has-top-controls', shown)
    reserve(shown)
  }

  /**
   * Publish the cluster's real width so the title bar can reserve exactly that
   * much and no more. A guessed constant was wrong the moment a second button
   * appeared: one cast target reserved too much, two reserved too little and
   * the title ran under the icons.
   */
  const reserve = (shown: boolean): void => {
    if (!shown) {
      playerEl.style.removeProperty('--lp-top-controls-w')
      return
    }
    const w = Math.ceil(cluster.getBoundingClientRect().width)
    if (w > 0) playerEl.style.setProperty('--lp-top-controls-w', `${w}px`)
  }

  const setCompact = (next: boolean): void => {
    if (next === compact) return
    compact = next
    sync()
  }

  const measure = (): void => {
    setCompact(playerEl.clientWidth > 0 && playerEl.clientWidth <= COMPACT_PX)
  }

  const observer = new ResizeObserver(measure)
  observer.observe(playerEl)
  // The cluster's own width changes as buttons arrive and leave, and the
  // reservation has to follow it.
  const clusterObserver = new ResizeObserver(() => reserve(playerEl.classList.contains('lp-has-top-controls')))
  clusterObserver.observe(cluster)
  measure()

  /*
    The bar is rebuilt on a tech switch and its children arrive over several
    seconds, so watching it is the only way to catch a button that did not
    exist at mount. Cheap: control bar mutations are rare and this only moves
    nodes that are already in the wrong place.
  */
  const barObserver = new MutationObserver(() => sync())
  const bar = controlBar()
  if (bar) barObserver.observe(bar, { childList: true, subtree: false, attributes: true, attributeFilter: ['class'] })
  const selfObserver = new MutationObserver(() => sync())
  selfObserver.observe(playerEl, { childList: true, subtree: false })

  return {
    dispose(): void {
      disposed = true
      observer.disconnect()
      clusterObserver.disconnect()
      barObserver.disconnect()
      selfObserver.disconnect()
      // Hand the buttons back before leaving: they belong to video.js, and a
      // rebuild that finds them parented to a removed div loses them.
      compact = false
      const back = controlBar()
      if (back) {
        for (const sel of LIFTABLE) {
          const btn = cluster.querySelector<HTMLElement>(sel)
          if (btn) back.insertBefore(btn, back.querySelector('.lp-settings-button'))
        }
      }
      cluster.remove()
      playerEl.classList.remove('lp-has-top-controls')
      playerEl.style.removeProperty('--lp-top-controls-w')
    },
  }
}
